<template>
  <div class="torque-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>τ = r × F | M = F · L$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Pivot -->
          <circle cx="100" cy="120" r="8" fill="#444" stroke="#888" stroke-width="2" />
          <circle cx="100" cy="120" r="2" fill="#fff" />

          <!-- Lever arm -->
          <rect :x="100" y="115" :width="armLength * 2" height="10" fill="#747d8c" rx="2" 
                :transform="`rotate(${angle}, 100, 120)`" />
          
          <!-- Force Point -->
          <g :transform="`translate(${forcePointX}, ${forcePointY}) rotate(${angle}, 0, 0)`">
             <!-- Force Arrow -->
             <line x1="0" y1="0" x2="0" :y2="forceMagnitude * 2" 
                   stroke="#ff4757" stroke-width="4" marker-end="url(#torque-arrow)" 
                   :transform="`rotate(${forceAngle}, 0, 0)`" />
             <text x="10" y="20" fill="#ff4757" font-size="12" font-weight="bold">F</text>
          </g>

          <!-- Rotation Indicator -->
          <path v-if="torqueValue !== 0" :d="rotationArc" fill="none" stroke="#2ed573" stroke-width="3" stroke-dasharray="5,3">
             <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2s" repeatCount="indefinite" />
          </path>

          <!-- Labels -->
          <text x="105" y="145" fill="#888" font-size="8">支点</text>
          <text :x="100 + armLength" y="105" fill="#888" font-size="8" :transform="`rotate(${angle}, 100, 120)`">
             力臂 L = {{ armLength }} cm
          </text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>力矩大小 M = F · L · sin(θ)</label>
              <div class="value" :style="{color: torqueValue === 0 ? '#888' : '#2ed573'}">
                 M = {{ Math.abs(torqueValue).toFixed(2) }} N·m
                 <span v-if="torqueValue > 0" style="font-size: 0.7rem; color: #aaa;"> (逆时针)</span>
                 <span v-else-if="torqueValue < 0" style="font-size: 0.7rem; color: #aaa;"> (顺时针)</span>
              </div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center gap-4">
            <div class="flex-1">
               <label>力臂长度 r (Distance)</label>
               <el-slider v-model="armLength" :min="10" :max="130" size="mini"></el-slider>
            </div>
            <div class="flex-1">
               <label>力的大小 F (Force)</label>
               <el-slider v-model="forceMagnitude" :min="0" :max="50" size="mini"></el-slider>
            </div>
          </div>
          <div class="row items-center gap-4 mt-2">
            <div class="flex-1">
               <label>力与杆的夹角 θ (Angle)</label>
               <el-slider v-model="forceAngle" :min="0" :max="180" size="mini"></el-slider>
            </div>
            <el-button type="primary" size="mini" @click="reset">复位位置</el-button>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>力矩是一个矢量，描述了力对物体的转动作用。力矩的大小不仅取决于**力的大小 (F)**，还取决于**支点到力作用线的垂直距离 (力臂 L)**。
           公式表示为 $M = r \times F$。当你沿着杆的切线方向施力（$\theta = 90^\circ$）时，力矩最大；而当你沿着杆的方向施力（$\theta = 0^\circ$）时，力矩为零。这是“给我一个支点，我就能撬动地球”这一著名论断的物理依据。</p>
        </div>
      </div>
    </div>

    <!-- Arrow Def -->
    <svg height="0" width="0">
       <defs>
          <marker id="torque-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
             <path d="M0,0 L6,3 L0,6 Z" fill="#ff4757" />
          </marker>
       </defs>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'TorqueViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      armLength: 80,
      forceMagnitude: 30,
      forceAngle: 90,
      angle: 0,
      timer: null
    }
  },
  computed: {
    torqueValue() {
      // theta is angle between force and lever arm
      // sin(forceAngle) because forceAngle is relative to the lever arm
      let rad = (this.forceAngle * Math.PI) / 180;
      // In SVG coordinates, 90 deg is perpendicular downwards
      return (this.forceMagnitude * this.armLength * Math.sin(rad)) / 100;
    },
    forcePointX() {
      let rad = (this.angle * Math.PI) / 180;
      return 100 + Math.cos(rad) * this.armLength * 2;
    },
    forcePointY() {
      let rad = (this.angle * Math.PI) / 180;
      return 120 + Math.sin(rad) * this.armLength * 2;
    },
    rotationArc() {
      const radius = 30;
      const startX = 100 + radius;
      const startY = 120;
      const dir = this.torqueValue > 0 ? 0 : 1; // Counter or Clockwise
      return `M ${startX} ${startY} A ${radius} ${radius} 0 1 ${dir} ${100-radius} ${startY}`;
    }
  },
  methods: {
    animate() {
       // Only rotate if there's torque and not at extreme limits (simulated friction)
       if (this.torqueValue !== 0) {
          this.angle += this.torqueValue * 0.1;
       }
       this.timer = requestAnimationFrame(this.animate);
    },
    reset() {
      this.angle = 0;
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.torque-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(255, 71, 87, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
